import { z } from 'zod';

import {
  isRadioQuestion,
  isRadioResponse,
  QuestionResponse,
  TypedQuestion,
  RadioQuestion,
  RadioQuestionResponse
} from 'types/question-response.types';

import { QuestionResponseFormValue, IsRequired, OTHER_OPTION } from '../base.types';

type BaseRadioQuestionResponseFormValue = QuestionResponseFormValue<
  RadioQuestionResponse,
  'selectedOption' | 'otherValue'
>;

type NonEmptyRadioQuestionResponseFormValue = BaseRadioQuestionResponseFormValue &
  (
    | { req: IsRequired.Req | IsRequired.Opt; selectedOption: string }
    | {
        req: IsRequired.Req | IsRequired.Opt;
        selectedOption: typeof OTHER_OPTION;
        otherValue: string;
      }
  );
export type FormValue = BaseRadioQuestionResponseFormValue | NonEmptyRadioQuestionResponseFormValue;

export const valueFromFormValue = (response: FormValue): string | undefined => {
  if (!isNonEmpty(response)) return undefined;
  if ('otherValue' in response) {
    return response.otherValue || undefined;
  } else {
    return response.selectedOption !== OTHER_OPTION ? response.selectedOption : undefined;
  }
};

export const isNonEmpty = (
  response: FormValue
): response is NonEmptyRadioQuestionResponseFormValue => {
  const { selectedOption } = response;
  if (!selectedOption) return false;
  return selectedOption !== OTHER_OPTION || 'otherValue' in response;
};

// Omit doesn't distribute over unions
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;

export const submissionValue = (
  response: NonEmptyRadioQuestionResponseFormValue
): DistributiveOmit<RadioQuestionResponse, 'id'> => {
  const { type, selectedOption } = response;
  const base = { type };
  if ('otherValue' in response && response.otherValue) {
    return { ...base, selectedOption: null, otherValue: response.otherValue };
  } else {
    return { ...base, selectedOption, otherValue: null };
  }
};

export const schema = ({ options, hasOtherOption, type }: RadioQuestion): z.ZodType<FormValue> => {
  const opts = hasOtherOption ? [...options, OTHER_OPTION] : options;
  const isAllowedAnswer = (selectedOption: string): boolean => {
    return opts.includes(selectedOption);
  };
  const baseSchema = z.object({ type: z.literal(type) });
  const valueSchema = z.discriminatedUnion('req', [
    z.object({
      selectedOption: z.string().nonempty('This question is required.').refine(isAllowedAnswer),
      req: z.literal(IsRequired.Req)
    }),
    z.object({
      selectedOption: z.string().refine(isAllowedAnswer).optional(),
      req: z.literal(IsRequired.Opt)
    })
  ]);
  if (hasOtherOption) {
    return baseSchema
      .extend({ otherValue: z.string().optional() })
      .and(valueSchema)
      .superRefine((data, ctx) => {
        if (data.selectedOption === OTHER_OPTION) {
          const { otherValue } = data;
          const isEmpty = otherValue === undefined || otherValue === null || otherValue === '';
          if (isEmpty) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'This field is required.',
              path: ['otherValue']
            });
          }
        }
      });
  } else {
    return baseSchema.and(valueSchema);
  }
};

export const initialize: (
  question: RadioQuestion,
  response?: RadioQuestionResponse
) => FormValue = ({ type, isRequired: required, options, hasOtherOption }, response) => {
  const req = required ? IsRequired.Req : IsRequired.Opt;
  const base = { type, req };
  if (!response) {
    return base;
  }
  const { selectedOption, otherValue } = response;
  const inOptions = selectedOption !== null && options.includes(selectedOption);
  if (inOptions) {
    return { ...base, selectedOption };
  }
  return hasOtherOption && otherValue !== null
    ? { ...base, selectedOption: OTHER_OPTION, otherValue }
    : base;
};

export const validateAndInitialize = (
  question: TypedQuestion,
  response?: QuestionResponse
): FormValue | undefined => {
  if (!isRadioQuestion(question)) return undefined;
  if (response && isRadioResponse(response)) return initialize(question, response);
  return initialize(question);
};

export const validateAndSchematize = (
  question: TypedQuestion
): z.ZodType<FormValue> | undefined => {
  return isRadioQuestion(question) ? schema(question) : undefined;
};

export const validateAndSerialize = (
  id: number,
  formValue: FormValue
): QuestionResponse | undefined => {
  if (!isNonEmpty(formValue)) return undefined;
  return { id, ...submissionValue(formValue) };
};
